/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {applicationRoleMapping, getApplicationRoles, sortFunction} from '@ion/core/src/entities/Roles';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {getApplications, getSubAccounts, getUserRoles} from '@ion/core/src/services/Connect';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  EntityStatus,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {TertiaryIconButton} from '@ion/dls/src/components/buttons/TertiaryIconButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {
  getDefaultUserRoles
} from '@ion/dls/src/editors/UserEditorUtils';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import ApplicationChooser from './ApplicationChooser';
import RoleChooser from './RoleChooser';

interface ApplicationLevel {
  id: number;
  applications: Link,
  roles: Link
}

export interface ApplicationsProps {
  onChange: (applications: GenericObject) => void;
  formState?: GenericObject;
  applications?: GenericObject;
  roles: any,
  context?: UserContext;
  accountId: number,
  classes?: string;
  vbusRoles?: string;
  readOnly: boolean;
  required: boolean;
  bottomBorder?: boolean;
  isRootAccount?: boolean;
  isCurrentUser?: boolean;
};

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px'
  },
  typoStyle : {
    fontSize : '14px !important',
    width    : '100%'
  },
  subHeader : {
    display : 'flex',
    width   : '100%',
    padding : '16px',
    height  : '0em'
  },
  deleteStyle : {
    top : '25px'
  },
  iconStyle : {
    margin   : '-32px',
    position : 'relative',
    top      : '55px',
    left     : '25px'
  },
  spinner : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  }
}));

/**
 *
 * The Applications component provides the UI which allows a user to update their default
 * application preferences, such as their default page, group, etc..
 *
 * Note: This component is intended to be used directly by the UserEditor and should not
 * be used in stand-alone mode.
 *
 * * ```typescript
 *
 *    import {Applications} from '@ion/features/src/entities/applications';
 *
 *    <Applications
 *      accountId={accountId}
 *      roles={roles}
 *      onChange={onChange} />
 *
 */
export const Applications = forwardRef(({
  readOnly,
  required,
  roles,
  onChange,
  accountId,
  context,
  formState,
  vbusRoles,
  applications = [],
  bottomBorder = true,
  isRootAccount = true,
  isCurrentUser = false
}: ApplicationsProps, ref) => {
  const classes = useStyles();
  const [userRoles, setUserRoles]               = useState([]);
  const [userApplications, setUserApplications] = useState<any>([]);
  const [applicationLevel, setApplicationLevel] = useState(applications);
  const [focusRoleField, setfocusRoleField]     = useState(false);
  const [isLoading, setIsLoading]               = useState<boolean>(false);
  const hasMounted                              = useHasMounted();
  const excludeRoles                            = !isRootAccount ? safeJSONParse(vbusRoles) : [];

  const onSelectApplication = function(application, {id}): void {
    const {applicationKey} = application || {};
    let filterRoles = getApplicationRoles(applicationKey, userRoles);
    filterRoles = filterRoles.length > 0 ? sortFunction(filterRoles, 'name') : filterRoles;
    const addLevel = applicationLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        const exists = application && applicationLevel.filter((items) =>
          items.applications.title === application.name);
        const isValid = exists?.length <= 0;
        item.roles =  {
          ...getDefaultUserRoles(),
          list : application ? (!isValid ? [] : filterRoles) : []
        };
        item.applications= {
          href   : isValid ? (application?._self?.href || '') : '',
          title  : isValid ? (application?.name || ''): '',
          rel    : isValid ? (application?._self?.rel || ''): '',
          status : isValid ? (application?.status || ''): '',
          error  : (!isValid && application) ? translate('common:features.applications.duplicationError') : ''
        };
      }
      return item;
    });
    setfocusRoleField(true);
    setApplicationLevel(addLevel);
  };
  const onSelectRoles = function(role, {id}): void {
    const addLevel = applicationLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        item.roles = {
          list   : item?.roles?.list || [],
          href   : role?._self?.href || '',
          rel    : role?._self?.rel || '',
          title  : role?.name || '',
          status : role?.status || '',
          error  : ''
        };
      }
      return item;
    });
    setApplicationLevel(addLevel);
  };

  /**
   * create new row for Applications i.e applications and roles
   */
  function createApplication(apps?: GenericObject, selectedRole?: GenericObject, selectedRoles?: GenericObject) {
    const level = Object.assign([], applicationLevel);
    const result = {
      id           : Math.floor(Math.random() * 1000),
      applications : {
        href   : apps?._self?.href || '',
        title  : apps?.name || '',
        rel    : apps?._self?.rel || '',
        status : apps?.status || '',
        error  : ''
      },
      roles : {
        list   : selectedRoles || [],
        href   : selectedRole?._self?.href || selectedRole?.href || '',
        rel    : selectedRole?._self?.rel || selectedRole?.rel || '',
        title  : selectedRole?.name || selectedRole?.title || '',
        status : selectedRole?.status || '',
        error  : ''
      }

    };
    level.push(result);
    setApplicationLevel(level);
    return result;
  }

  /**
   * create new row for Applications i.e applications and roles
   */
  function deleteApplication({id}) {
    if (applicationLevel.length !== 1) {
      const level = applicationLevel?.filter((item) => Number(item.id) !== Number(id));
      setApplicationLevel(level);
    }
  }

  // manages loading applications based on current search term ...
  const loadApplicationData = async function() {
    setIsLoading(true);
    let rolesResults;
    let rolesResult;
    let applicationsResults;
    try {
      rolesResults = (context=== UserContext.ctcAdmin) ? await getUserRoles(accountId) : roles;
      applicationsResults = (context=== UserContext.ctcAdmin)
        ? await getApplications(accountId) : getSubAccounts(accountId);
    } catch (err) {
      Error('Applications: Error occurred retrieving Appications');
    } finally {
      if (hasMounted !== false) {
        rolesResult = rolesResults?.filter((role) => !excludeRoles.includes(role?.id));
        applicationsResults = sortFunction(applicationsResults, 'name');
        setUserApplications(applicationsResults);
        setUserRoles(rolesResult);
        setIsLoading(false);
        if (formState?.applications?.value) {
          const {applications: apps, roles: roleObj} = formState?.applications?.value;
          const appLevel = [];
          apps?.forEach(app => {
            const {title} = app;
            const application = applicationsResults?.filter(({name}) => name === title)?.[0];
            const {selectedRole, filteredRoles} = applicationRoleMapping(title, roleObj, rolesResult);
            if (application && application.status === EntityStatus.Enabled) {
              appLevel.push(createApplication(application, selectedRole || roleObj[0], filteredRoles));
              setApplicationLevel(appLevel);
            }
          });
        }
      }
    }
  };

  //Refreshing the application
  useEffect(() => {
    onChange(applicationLevel);
  }, [applicationLevel]);

  //Pre load both Application and Roles
  useEffect(() => {
    loadApplicationData();
  }, [accountId]);

  return (
    <div className={classes.container}>
      <FormSection bottomBorder={bottomBorder}
        title={translate('common:features.applications.label')}
        legend={!readOnly && <TertiaryButton onClick={createApplication}>
          {translate('common:features.applications.add')}</TertiaryButton>}>
        {
          (isCurrentUser && isLoading) ? (
            <div className={classes.spinner}>
              <IonSpinner width='25px' height='25px' />
            </div>
          ) : (
            applicationLevel?.map((item) => {
              return (<>
                <FormItem disableMinWidth>
                  <ApplicationChooser key={item.id}
                    required={required}
                    keyObj={item}
                    loading={isLoading}
                    userApplications={userApplications}
                    accountId={accountId}
                    readOnly={readOnly}
                    onChange={onSelectApplication}
                    validationError={(item.applications.error)
                      ? (item.applications.error !== '' ? item.applications.error
                        : translate('common:features.applications.error')) : ''}
                    defaultValue={item.applications.title}/>
                </FormItem>
                <FormItem disableMinWidth>
                  <RoleChooser key={item.id}
                    required={required}
                    keyObj={item}
                    loading={isLoading}
                    readOnly={readOnly || item.applications.title === ''}
                    roles={item.roles.list}
                    onChange={onSelectRoles}
                    focusRoleField={focusRoleField}
                    validationError={(item.roles.error !== '')
                      ? translate('common:features.roles.roleError') : ''}
                    defaultValue ={item.roles.title}/>
                </FormItem>
                {(applicationLevel?.length > 1 && !readOnly) && <div className={classes.iconStyle}>
                  <FormItem disableMinWidth>
                    <TertiaryIconButton icon={<SystemDeleteIcon />}
                      className={classes.deleteStyle}onClick={function(e) {
                        deleteApplication(item);
                      }} />
                  </FormItem>
                </div>}
              </>
              );
            })
          )
        }
      </FormSection>
    </div>
  );
});

export default Applications;
